import { QueryClient, VueQueryPlugin, dehydrate, hydrate } from '@tanstack/vue-query'
import { watch } from 'vue'

// Query keys zależne od sesji PrestaShop (cookie PrestaShop-XXX). Nie mogą trafiać
// do dehydrated payloadu, bo strony z `swr` w routeRules cachują pełny HTML wraz
// z payloadem — w SWR HIT pierwszy anonimowy SSR zapisuje pusty cart i kolejni
// użytkownicy dostają cudzy/pusty state w hydrate. Wykluczamy je tutaj, dzięki
// czemu klient po hydration fetchuje świeże dane ze swoimi cookies.
const userScopedKeyPrefixes: readonly (readonly string[])[] = [
  ['store', 'cart'],
  ['store', 'wishlist'],
  ['store', 'orders'],
  ['store', 'returns'],
  ['store', 'account'],
  ['store', 'guest-session'],
  ['store', 'auth'],
  ['store', 'checkout'],
  ['store', 'products', 'stock-alerts'],
  ['inpost', 'cart-choice'],
]

function isUserScopedQuery(queryKey: readonly unknown[]) {
  return userScopedKeyPrefixes.some((prefix) => {
    if (queryKey.length < prefix.length) return false
    return prefix.every((part, index) => queryKey[index] === part)
  })
}

export default defineNuxtPlugin((nuxt) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  })

  nuxt.vueApp.use(VueQueryPlugin, { queryClient })

  if (import.meta.server) {
    nuxt.hooks.hook('app:rendered', () => {
      nuxt.payload.vueQueryState = dehydrate(queryClient, {
        shouldDehydrateQuery: query => query.state.status === 'success' && !isUserScopedQuery(query.queryKey),
      })
      // Fix wycieku pamięci SSR (cz.1 — QueryCache): na serwerze query-core wymusza
      // gcTime=Infinity, więc QueryCache NIGDY nie usuwa Query/QueryObserver z tego
      // per-request klienta. clear() PO dehydrate opróżnia queryCache + mutationCache.
      // Payload (vueQueryState) jest już zserializowany powyżej, więc hydracja po
      // stronie klienta pozostaje bez zmian.
      // Najpierw jawnie niszczymy obserwery (zrywają subskrypcje observer→Query→#client).
      try {
        for (const q of queryClient.getQueryCache().getAll()) {
          for (const o of [...q.observers]) (o as { destroy?: () => void }).destroy?.()
        }
      }
      catch { /* defensywnie — nigdy nie wywracaj renderu */ }
      queryClient.clear()

      // Fix wycieku pamięci SSR (cz.2 — graf reaktywny komponentów): to jest właściwa
      // przyczyna OOM. clear() zwalnia tylko cache (Query ~0.23MB), ale 442MB Dep/Link
      // należy do grafu reaktywnego komponentów Vue, trzymanego przez per-request
      // nuxtApp (znany leak Nuxt SSR: nuxt#34071/#33080 — onServerPrefetch(suspense)
      // odpina EffectScope, a createNuxtAppServer nigdy nie woła _scope.stop()/unmount).
      // app:rendered odpala się PO renderToString — HTML i payload są już złapane, więc
      // demontaż per-request aplikacji Vue jest bezpieczny dla odpowiedzi i niszczy
      // scope'y komponentów (→ onScopeDispose, dispose efektów/computed → zwolnienie
      // Dep/Link i całego per-request nuxtApp).
      try {
        nuxt.vueApp.unmount()
      }
      catch { /* defensywnie */ }
    })
  }

  if (import.meta.client) {
    hydrate(queryClient, nuxt.payload.vueQueryState)
    // Re-hydrate on SPA navigation: nuxt.payload updates with _payload.json data
    // before the new page component mounts, so this fires before queries start.
    watch(() => nuxt.payload.vueQueryState, (state) => {
      if (state) hydrate(queryClient, state)
    })
  }
})
